import React, { useState } from 'react';
import { DynamicIcon } from '../icons/DynamicIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface TabOrcamentoFinalProps {
  eventoData: {
    nomeEvento: string;
    tipoEvento: string;
    dataEvento: string;
    horarioInicio: string;
    horarioTermino: string;
    localEvento: string;
    numeroConvidados: number;
    nivelConsumo: string;
    drinksEscolhidos: Array<{
      id: string;
      name: string;
      quantity: number;
      is_standard: boolean;
    }>;
    bartenders: number;
    assistentes: number;
    complementos: {
      acucar: boolean;
      adocante: boolean;
      gelo: boolean;
      canudo: boolean;
      tipoCopos: string;
      balcao: boolean;
    };
    margemLucro: number;
    tema: {
      nome: string;
      cores: {
        primaria: string;
        secundaria: string;
        fundo: string;
        superficie: string;
        textoPrincipal: string;
        textoSecundario: string;
      }
    }
    nomeEvento: string;
    tipoEvento: string;
    dataEvento: string;
    horarioInicio: string;
    horarioTermino: string;
    localEvento: string;
    numeroConvidados: number;
    nivelConsumo: string;
    drinksEscolhidos: Array<{
      id: string;
      name: string;
      quantity: number;
      is_standard: boolean;
    }>;
    bartenders: number;
    assistentes: number;
    complementos: {
      acucar: boolean;
      adocante: boolean;
      gelo: boolean;
      canudo: boolean;
      tipoCopos: string;
      balcao: boolean;
    };
    margemLucro: number;
  };
}

const TabOrcamentoFinal: React.FC<TabOrcamentoFinalProps> = ({ eventoData }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  
  // Valores base para cálculos
  const valorHoraBartender = 50;
  const valorHoraAssistente = 30;
  
  // Níveis de consumo e seus multiplicadores
  const niveisConsumo = {
    baixo: { drinks: 2, valorBase: 15 },
    medio: { drinks: 4, valorBase: 20 },
    alto: { drinks: 6, valorBase: 25 }
  };

  // Calcular duração em horas
  const calcularDuracaoHoras = () => {
    if (eventoData.horarioInicio && eventoData.horarioTermino) {
      const [horaInicio, minInicio] = eventoData.horarioInicio.split(':').map(Number);
      const [horaFim, minFim] = eventoData.horarioTermino.split(':').map(Number);
      
      // Converter para minutos para facilitar o cálculo
      let inicioMinutos = horaInicio * 60 + minInicio;
      let fimMinutos = horaFim * 60 + minFim;
      
      // Se o horário de término for menor que o de início, assumimos que é no dia seguinte
      if (fimMinutos < inicioMinutos) {
        fimMinutos += 24 * 60; // Adiciona 24 horas em minutos
      }
      
      const duracaoMinutos = fimMinutos - inicioMinutos;
      return duracaoMinutos / 60;
    }
    return 0;
  };

  // Calcular custo dos drinks
  const calcularCustoDrinks = () => {
    const nivel = niveisConsumo[eventoData.nivelConsumo as keyof typeof niveisConsumo];
    const drinksTotal = eventoData.numeroConvidados * nivel.drinks;

    // Se não há drinks selecionados, usar valor base
    if (eventoData.drinksEscolhidos.length === 0) {
      return drinksTotal * nivel.valorBase;
    }

    // Calcular com base nos drinks selecionados
    const totalQuantity = eventoData.drinksEscolhidos.reduce((sum, drink) => sum + drink.quantity, 0);
    let custoTotal = 0;

    eventoData.drinksEscolhidos.forEach(drink => {
      const proporcao = drink.quantity / totalQuantity;
      const drinksPorTipo = drinksTotal * proporcao;

      // Valor base por drink, premium custa mais
      const valorPorDrink = drink.is_standard ? nivel.valorBase : nivel.valorBase * 1.5;
      custoTotal += drinksPorTipo * valorPorDrink;
    });

    return custoTotal;
  };

  // Calcular custo da equipe
  const calcularCustoEquipe = () => {
    const duracao = calcularDuracaoHoras();
    const custoBartenders = eventoData.bartenders * valorHoraBartender * duracao;
    const custoAssistentes = eventoData.assistentes * valorHoraAssistente * duracao;
    return custoBartenders + custoAssistentes;
  };

  // Calcular custo dos complementos
  const calcularCustoComplementos = () => {
    const { complementos } = eventoData;
    const convidados = eventoData.numeroConvidados;

    let custoTotal = 0;

    // Valores base para complementos
    const valorComplementos = {
      acucar: 0.5,
      adocante: 0.8,
      gelo: 2,
      canudo: 0.3,
      balcao: 150,
      copos: {
        plastico: 0.5,
        acrilico: 2,
        vidro: 5
      }
    };

    if (complementos.acucar) custoTotal += valorComplementos.acucar * convidados;
    if (complementos.adocante) custoTotal += valorComplementos.adocante * convidados;
    if (complementos.gelo) custoTotal += valorComplementos.gelo * convidados;
    if (complementos.canudo) custoTotal += valorComplementos.canudo * convidados;
    if (complementos.balcao) custoTotal += valorComplementos.balcao;

    // Adicionar custo dos copos
    const tipoCopos = complementos.tipoCopos as keyof typeof valorComplementos.copos;
    custoTotal += valorComplementos.copos[tipoCopos] * convidados;

    return custoTotal;
  };

  // Calcular subtotal
  const calcularSubtotal = () => {
    return calcularCustoDrinks() + calcularCustoEquipe() + calcularCustoComplementos();
  };

  // Calcular total com margem
  const calcularTotal = () => {
    const subtotal = calcularSubtotal();
    return subtotal * (1 + eventoData.margemLucro / 100);
  };

  // Formatar valor em reais
  const formatarValor = (valor: number) => {
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  // Formatar data
  const formatarData = (data: string) => {
    if (!data) return '';
    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  // Salvar orçamento
  const salvarOrcamento = async () => {
    if (!user) {
      toast.error('Você precisa estar logado para salvar o orçamento');
      return;
    }

    if (!eventoData.nomeEvento) {
      toast.error('Preencha o nome do evento');
      return;
    }

    try {
      setLoading(true);
      
      const subtotal = calcularSubtotal();
      const total = calcularTotal();
      
      const { data, error } = await supabase
        .from('eventpour_orcamentos')
        .insert([
          {
            cliente_nome: eventoData.nomeEvento,
            data_evento: eventoData.dataEvento,
            tipo_evento: eventoData.tipoEvento,
            numero_convidados: eventoData.numeroConvidados,
            valor_total: calcularTotal(),
            status: 'rascunho',
            user_id: user?.id
          }
        ]);

      if (error) throw error;
      
      toast.success('Orçamento salvo com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar orçamento');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Compartilhar via WhatsApp
  const compartilharWhatsApp = () => {
    const texto = 
      `*Orçamento - ${eventoData.nomeEvento}*\n\n` +
      `📅 Data: ${formatarData(eventoData.dataEvento)}\n` +
      `🎯 Tipo: ${eventoData.tipoEvento}\n` +
      `👥 Convidados: ${eventoData.numeroConvidados}\n` +
      `💰 Valor Total: ${formatarValor(calcularTotal())}\n\n` +
      `Inclui ${eventoData.bartenders} bartender(s) e ${eventoData.assistentes} assistente(s).\n\n` +
      `Gerado pelo EventPour - Calculadora de Orçamentos para Bartenders`;
    
    const url = `https://wa.me/?text=${encodeURIComponent(texto)}`;
    window.open(url, '_blank');
    setShowShareOptions(false);
  };

  // Gerar PDF
  const gerarPDF = () => {
    toast.success('Funcionalidade de PDF em desenvolvimento');
    
    // Aqui você aplicaria as cores do tema ao gerar o PDF
    const tema = eventoData.tema.cores;
    
    // Exemplo de estrutura para o PDF com as cores personalizadas
    const pdfConfig = {
      tema: tema,
      dados: {
        evento: eventoData.nomeEvento,
        data: eventoData.dataEvento,
        // ... outros dados
      }
    };
    
    // Implementar geração de PDF com as cores
    console.log('Gerando PDF com tema:', tema);
    
    alert('PDF será gerado com as cores personalizadas!');
    setShowShareOptions(false);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-bold mb-6 flex items-center">
        <DynamicIcon name="fileText" size={24} className="mr-2 text-blue-600" />
        Orçamento Final
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Detalhes do Evento */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium mb-4 text-gray-900 flex items-center">
            <DynamicIcon name="calendar" size={20} className="mr-2 text-blue-600" />
            Detalhes do Evento
          </h3>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Nome do Evento:</span>
              <span className="font-medium">{eventoData.nomeEvento || 'Não informado'}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Tipo:</span>
              <span className="font-medium">{eventoData.tipoEvento.charAt(0).toUpperCase() + eventoData.tipoEvento.slice(1)}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Data:</span>
              <span className="font-medium">{formatarData(eventoData.dataEvento) || 'Não informada'}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Horário:</span>
              <span className="font-medium">{eventoData.horarioInicio} às {eventoData.horarioTermino}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Local:</span>
              <span className="font-medium">{eventoData.localEvento || 'Não informado'}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Convidados:</span>
              <span className="font-medium">{eventoData.numeroConvidados}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Duração:</span>
              <span className="font-medium">{calcularDuracaoHoras().toFixed(1)}h</span>
            </div>
          </div>
        </div>
        
        {/* Drinks e Consumo */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium mb-4 text-gray-900 flex items-center">
            <DynamicIcon name="glasses" size={20} className="mr-2 text-blue-600" />
            Drinks e Consumo
          </h3>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Nível de Consumo:</span>
              <span className="font-medium">{eventoData.nivelConsumo.charAt(0).toUpperCase() + eventoData.nivelConsumo.slice(1)}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Drinks por Pessoa:</span>
              <span className="font-medium">{niveisConsumo[eventoData.nivelConsumo as keyof typeof niveisConsumo].drinks}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Total de Drinks:</span>
              <span className="font-medium">{eventoData.numeroConvidados * niveisConsumo[eventoData.nivelConsumo as keyof typeof niveisConsumo].drinks}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Drinks Selecionados:</span>
              <span className="font-medium">{eventoData.drinksEscolhidos.length}</span>
            </div>
            
            {eventoData.drinksEscolhidos.length > 0 && (
              <div className="mt-2">
                <div className="text-gray-600 mb-1">Lista de Drinks:</div>
                <ul className="text-sm space-y-1">
                  {eventoData.drinksEscolhidos.map((drink, index) => (
                    <li key={index} className="flex justify-between">
                      <span>{drink.name}</span>
                      <span className="text-gray-500">
                        {drink.quantity}x {drink.is_standard ? '(Standard)' : '(Premium)'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Equipe e Complementos */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium mb-4 text-gray-900 flex items-center">
            <DynamicIcon name="users" size={20} className="mr-2 text-blue-600" />
            Equipe e Complementos
          </h3>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Bartenders:</span>
              <span className="font-medium">{eventoData.bartenders}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Assistentes:</span>
              <span className="font-medium">{eventoData.assistentes}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Tipo de Copos:</span>
              <span className="font-medium">
                {eventoData.complementos.tipoCopos === 'plastico' && 'Copos de Plástico'}
                {eventoData.complementos.tipoCopos === 'acrilico' && 'Copos de Acrílico'}
                {eventoData.complementos.tipoCopos === 'vidro' && 'Taças de Vidro'}
              </span>
            </div>
            
            <div className="mt-2">
              <div className="text-gray-600 mb-1">Complementos Inclusos:</div>
              <ul className="text-sm space-y-1">
                {eventoData.complementos.acucar && (
                  <li className="flex items-center">
                    <DynamicIcon name="check" size={16} className="mr-2 text-green-500" />
                    Açúcar
                  </li>
                )}
                {eventoData.complementos.adocante && (
                  <li className="flex items-center">
                    <DynamicIcon name="check" size={16} className="mr-2 text-green-500" />
                    Adoçante
                  </li>
                )}
                {eventoData.complementos.gelo && (
                  <li className="flex items-center">
                    <DynamicIcon name="check" size={16} className="mr-2 text-green-500" />
                    Gelo
                  </li>
                )}
                {eventoData.complementos.canudo && (
                  <li className="flex items-center">
                    <DynamicIcon name="check" size={16} className="mr-2 text-green-500" />
                    Canudos
                  </li>
                )}
                {eventoData.complementos.balcao && (
                  <li className="flex items-center">
                    <DynamicIcon name="check" size={16} className="mr-2 text-green-500" />
                    Balcão Móvel
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
        
        {/* Resumo Financeiro */}
        <div className="bg-blue-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium mb-4 text-blue-900 flex items-center">
            <DynamicIcon name="dollarSign" size={20} className="mr-2 text-blue-600" />
            Resumo Financeiro
          </h3>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-blue-700">Custo dos Drinks:</span>
              <span className="font-medium">{formatarValor(calcularCustoDrinks())}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-blue-700">Custo da Equipe:</span>
              <span className="font-medium">{formatarValor(calcularCustoEquipe())}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-blue-700">Custo dos Complementos:</span>
              <span className="font-medium">{formatarValor(calcularCustoComplementos())}</span>
            </div>
            
            <div className="flex justify-between border-t border-blue-200 pt-2 mt-2">
              <span className="text-blue-700 font-medium">Subtotal:</span>
              <span className="font-medium">{formatarValor(calcularSubtotal())}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-blue-700">Margem de Lucro ({eventoData.margemLucro}%):</span>
              <span className="font-medium">{formatarValor(calcularSubtotal() * eventoData.margemLucro / 100)}</span>
            </div>
            
            <div className="flex justify-between border-t border-blue-200 pt-3 mt-3 text-xl font-bold">
              <span className="text-blue-900">TOTAL:</span>
              <span className="text-blue-900">{formatarValor(calcularTotal())}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Ações */}
      <div className="flex flex-col md:flex-row gap-4 mt-6">
        <button
          onClick={salvarOrcamento}
          disabled={loading}
          className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {loading ? (
            <>
              <DynamicIcon name="loader" size={20} className="animate-spin mr-2" />
              Salvando...
            </>
          ) : (
            <>
              <DynamicIcon name="save" size={20} className="mr-2" />
              Salvar Orçamento
            </>
          )}
        </button>
        
        <div className="flex-1 relative">
          <button
            onClick={() => setShowShareOptions(!showShareOptions)}
            className="w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 flex items-center justify-center"
          >
            <DynamicIcon name="share" size={20} className="mr-2" />
            Compartilhar
          </button>
          
          {showShareOptions && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg z-10">
              <button
                onClick={compartilharWhatsApp}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center"
              >
                <DynamicIcon name="messageSquare" size={20} className="mr-2 text-green-600" />
                WhatsApp
              </button>
              <button
                onClick={gerarPDF}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center"
              >
                <DynamicIcon name="fileText" size={20} className="mr-2 text-red-600" />
                Gerar PDF
              </button>
            </div>
          )}
        </div>
        
        <button
          onClick={() => window.print()}
          className="flex-1 bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 flex items-center justify-center"
        >
          <DynamicIcon name="printer" size={20} className="mr-2" />
          Imprimir
        </button>
      </div>
    </div>
  );
};

export default TabOrcamentoFinal;